import { forwardRef, useImperativeHandle, useRef } from "react"
import Color, { ColorInstance } from "color"
import { ChevronDown, ChevronRight, GripVertical } from "lucide-react"
import { DotFilledIcon } from "@radix-ui/react-icons"
import { SvgIconProps } from "@mui/material"
import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBoxOutlined } from "@mui/icons-material"
import { mergeClasses } from "@/helpers/styling"
import { DynamicIcon } from "@/components/DynamicIcon"
import { SimpleTooltip } from "@/components/ui-primitives/simple-tooltip"
import { CheckState, TreeItem, TreeNode } from "../model"

export type TreeNodeChromeHandle = {
  row: HTMLDivElement | null,
  dragHandle: HTMLButtonElement | null
}

/**
 * Just the visual styling and L&F of the tree node
 */
export const TreeNodeChrome = forwardRef<TreeNodeChromeHandle, {
  node: TreeNode
  item: TreeItem
  variant: "default" | "medium" | "compact"
  checkboxLayout: "nested" | "aligned"
  depth: number
  selected?: boolean
  withCheckbox?: boolean
  withChevron?: boolean
  withDragHandle?: boolean
  withButtons?: boolean
  checkState?: CheckState
  onSetChecked?: (checked: boolean) => void
  expanded?: boolean
  onSetExpanded?: (expanded: boolean) => void
  classNames?: {
    wrapper?: string
    row?: string
  },
  renderDropInstruction?: () => React.ReactNode
  renderButtons?: () => React.ReactNode
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
}>(function TreeNodeChrome(props, ref) {
  const isSelected = Boolean(props.selected)
  const classNames = props.classNames ?? {}
  const colors = generateColors(
    props.item.label.color ? Color(props.item.label.color) : null,
    isSelected
  )

  const depthPadding = `${(props.depth * 16).toFixed(1)}px`
  const chevronPadding = props.checkboxLayout === "aligned" ? depthPadding : '0px'

  const rowRef = useRef<HTMLDivElement>(null)
  const dragHandleRef = useRef<HTMLButtonElement>(null)

  useImperativeHandle(ref, () => ({
    row: rowRef.current,
    dragHandle: dragHandleRef.current,
  }))

  const iconSize = (() => {
    switch (props.variant) {
      case "compact": return 14
      case "medium": return 16
      default: return 18
    }
  })()
  return (
    <div
      className={mergeClasses("flex w-full position-relative bg-white-0", classNames.wrapper)}
      style={{
        paddingLeft: props.checkboxLayout === "nested" ? depthPadding : undefined,
      }}>
      <div
        ref={rowRef}
        role="row"
        aria-label={props.item.label.text}
        aria-selected={props.selected}
        data-item-id={props.node.itemId}
        data-node-id={props.node.id}
        tabIndex={0}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = colors.hoverBgColor.hex()
          props.onMouseEnter?.(e)
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = colors.bgColor.hex()
          props.onMouseLeave?.(e)
        }}
        onClick={props.onClick}
        className={mergeClasses(`
          flex flex-grow w-full gap-1 py-[0.4em] pr-2 pl-1 items-center position-relative
          border-b border-gray-200
          text-gray-800 font-medium font-sans
          user-select-none
          active:shadow-[inset_0_0_0_1px_#d1c8ff]
          transition-colors
          `, {
            "py-[0.4em]": props.variant === "default",
            "py-[0.2em]": props.variant === "medium",
            "py-[0.15em]": props.variant === "compact",
            "text-[13px]": props.variant === "medium",
            "text-xs": props.variant === "compact",
          }, classNames.row
        )}
        style={{
          backgroundColor: colors.bgColor.hex(),
          borderBottomColor: isSelected ? colors.accentBorderColor.hex() : undefined,
        }}>
        {props.renderDropInstruction?.()}
        {props.withDragHandle && (
          <button ref={dragHandleRef} className="opacity-25 hover:opacity-50 py-[1em] my-[-1em] hover:bg-gray-100">
            <GripVertical size={14} />
          </button>
        )}
        {props.withCheckbox && (
          <label
            role="checkbox"
            aria-checked={CHECK_STATE_TO_ARIA[props.checkState ?? "unchecked"]}
            className={mergeClasses(`
              flex items-center m-0
              cursor-pointer
              text-gray-600 hover:text-wistor transition-colors            
            `, {
              "pl-[0.5em]": !props.withDragHandle,
              "text-wistor-alt": props.checkState !== "unchecked"
            })}
            onClick={e => {
              // don't trigger selection
              e.stopPropagation()
            }}
          >
            <input type="checkbox"
                   className="hidden"
                   data-check-state={props.checkState}
                   checked={props.checkState === "checked"}
                   onChange={e => {
                     props.onSetChecked?.(e.currentTarget.checked)
                   }}
            />
            <CheckBoxStateIcon checkState={props.checkState ?? "unchecked"}
                               className={mergeClasses("!text-base xmt-[2px]", {
                                 "!h-4 !w-4": props.variant === "default",
                                 "!h-3 !w-3": props.variant === "medium" || props.variant === "compact"
                               })} />
          </label>
        )}
        {props.withChevron ? (
          <button
            type="button"
            title={props.expanded ? 'Collapse' : 'Expand'}
            aria-expanded={props.expanded ? 'true' : 'false'}
            className="flex"
            onClick={e => {
              e.stopPropagation() // don't trigger selection
              props.onSetExpanded?.(!props.expanded)
            }}
            style={{ marginLeft: chevronPadding }}>
              <span
                className={mergeClasses(
                  "text-gray-600 hover:text-black rounded-full hover:bg-gray-100 flex items-center justify-center", {
                    "h-8 w-8": props.variant === "default",
                    "h-6 w-6": props.variant === "medium",
                    "h-5 w-5": props.variant === "compact",
                  },
                )}>
                {props.expanded
                  ? <ChevronDown size={iconSize} />
                  : <ChevronRight size={iconSize} />}
              </span>
          </button>
        ) : (
          <div className={mergeClasses({
            "min-h-8 min-w-8": props.variant === "default",
            "min-h-6 min-w-6": props.variant === "medium",
            "min-h-5 min-w-5": props.variant === "compact"
          })} style={{ marginLeft: chevronPadding }} />
        )}

        <SimpleTooltip tooltip={props.item.tooltip}>{/* replace by SimpleTooltip soon */}
          <div className="flex-grow inline-flex items-center min-w-0 overflow-hidden gap-1">
            {!!props.item.icon.name && (
              <div className="inline-flex justify-center"
                   style={{ minWidth: '28px' /* align icon variations */ }}>
                <DynamicIcon
                  icon={props.item.icon.name}
                  size={props.item.icon.size}
                  color={props.item.icon.color}
                />
              </div>
            )}
            <label className="cursor-pointer text-ellipsis text-nowrap overflow-hidden min-w-0 m-0"
                   style={{ color: props.item.label.color }}>
              {props.item.label.text ?? ''}
            </label>
          </div>
        </SimpleTooltip>
        {!!(props.item.rightLabel.text || props.item.rightIcon.name || props.item.rightIcon.color) && (
          <div className="
              inline-flex min-w-0 gap-1 pl-2
              justify-end items-center
              text-right overflow-hidden
              text-xs text-opacity-80 text-gray-500
              " style={{ gap: props.item.rightIcon.size ? `calc(min(.5rem, ${props.item.rightIcon.size} / 2))` : undefined }}>
            {props.item.rightLabel.text && (
              <span
                className="min-w-0 text-ellipsis text-nowrap overflow-hidden"
                style={{ color: props.item.rightLabel.color }}>
                {props.item.rightLabel.text}
                </span>
            )}
            {(props.item.rightIcon.color || props.item.rightIcon.name) && (
              <span className="mt-[1px]" style={{ minWidth: '15px' /* align icon variations */ }}>
                  {props.item.rightIcon.name
                    ? (
                      <DynamicIcon
                        icon={props.item.rightIcon.name}
                        size={props.item.rightIcon.size}
                        color={props.item.rightIcon.color}
                      />
                    ) : (
                      <DotFilledIcon style={{ color: props.item.rightIcon.color }} />
                    )}
                </span>
            )}
          </div>
        )}
        {props.withButtons && (
          <div className="flex-shrink-0 inline-flex justify-end items-end overflow-hidden">
            {props.renderButtons?.()}
          </div>
        )}
      </div>
    </div>
  )
})

function CheckBoxStateIcon(props: {
  checkState: CheckState,
} & SvgIconProps) {
  const { checkState, ...iconProps } = props
  switch (checkState) {
    case 'checked':
      return <CheckBox {...iconProps} />
    case 'unchecked':
      return <CheckBoxOutlineBlank {...iconProps} />
    case 'indeterminate':
      return <IndeterminateCheckBoxOutlined {...iconProps} />
  }
}

const CHECK_STATE_TO_ARIA: Record<CheckState, "true" | "false" | "mixed"> = {
  "checked": "true",
  "unchecked": "false",
  "indeterminate": "mixed"
} as const

function generateColors(color: ColorInstance | null, isSelected: boolean) {
  const isGreyscale = !color || color.saturationv() === 0.0
  const baseColor = isGreyscale ? Color('#095390') : color
  const baseHue = baseColor.hue()
  const baseSaturation = Math.min(100, baseColor.saturationl() * 4)

  const hoverBgColor = isSelected
    ? Color.hsl(baseHue, .7 * baseSaturation, 85)
    : Color.hsl(baseHue, .5 * baseSaturation, 95)
  const selectedBgColor = Color.hsl(baseHue, .7 * baseSaturation, 92)
  const accentBorderColor = Color.hsl(baseHue, .5 * baseSaturation, 40)
  const bgColor = isSelected ? selectedBgColor : Color('#ffffff')

  return {
    bgColor,
    hoverBgColor,
    accentBorderColor,
  } as const
}
